import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Input, Form, Message, Header, Segment, Divider } from 'semantic-ui-react';
import { useSignedS3Url } from '../hooks/useSignedS3Url';
import { HTTP_METHODS, RESOURCE_TYPES } from '../apiEngine/schemas/parameterSchema';

/**
 * Test component for demonstrating the simplified signed URL functionality
 * This component allows the user to enter an S3 URL and get a signed URL for it
 */
const SignedUrlTest = () => {
  const [url, setUrl] = useState('');
  const [signedUrl, setSignedUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Get the user ID from the Redux store
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.data?.userId || ''; //"91355c8b-c056-4861-979c-dc8bb4a1e1bf"; //
  console.log('SignedUrlTest userId', userId)
  // Use our new hook
  const { getSignedUrl } = useSignedS3Url();

  // Reset states when URL changes
  useEffect(() => {
    setSignedUrl('');
    setError('');
    setSuccess(false);
  }, [url]);

  const handleSubmit = async () => {
    if (!url) {
      setError('Please enter a URL');
      return;
    }

    if (!userId) {
      setError('User ID not available');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const result = await getSignedUrl(url, userId);

      if (result) {
        setSignedUrl(result);
        setSuccess(true);
      } else {
        setError('Failed to get signed URL');
      }
    } catch (error) {
      setError(`Error: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Segment>
      <Header as="h2">Signed URL Test</Header>
      <p>
        This component demonstrates the simplified signed URL functionality.
        Enter an S3 URL below to get a signed URL for it.
      </p>

      <Form onSubmit={handleSubmit}>
        <Form.Field>
          <label>S3 URL</label>
          <Input
            placeholder="Enter S3 URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            fluid
          />
        </Form.Field>

        <Button
          primary
          type="submit"
          loading={loading}
          disabled={loading || !url || !userId}
        >
          Get Signed URL
        </Button>
      </Form>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <div>
          <Divider />
          <Header as="h3">Result</Header>
          <p>
            <strong>Original URL:</strong> {url}
          </p>
          <p>
            <strong>Signed URL:</strong> {signedUrl}
          </p>

          {/* Add a link to open the signed URL */}
          <Button
            as="a"
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            color="green"
          >
            Open Signed URL
          </Button>
        </div>
      )}
    </Segment>
  );
};

export default SignedUrlTest;
